import { interaction, SizableText, YStack, YStackProps } from '@mythical/ui'
import {
  getQuarterCode,
  QuarterCodeText,
} from 'app/components/primitive/QuarterCodeText'
import { ColorEnum, mapColor } from 'app/utils/mapColors'
import { SmartLink } from 'app/components/primitive/SmartLink'
import { Tag } from 'app/components/primitive/Tag/Tag'
import { SolitoImage } from 'solito/image'

export type CollectibleProps = {
  id: string
  title: string
  quarter: string
  image: string
  href: string
  hasContent?: boolean
  availableFrom?: string | null
  availableUntil?: string | null
  backgroundColour?: ColorEnum
  isPastByPosition?: boolean
  isActive?: boolean
} & YStackProps

function getQuarterYear(quarter?: string): string {
  if (!quarter) return ''
  const match = quarter.match(/\b\d{4}\b/)
  return match?.[0] || ''
}

type DropState = 'latest' | 'past' | 'pending'

const QUARTER_TEXT_COLOR = '$brandPurple'

function toDate(date?: string | null): Date | null {
  if (!date) return null
  const parsedDate = new Date(date)
  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }
  return parsedDate
}

function resolveDropState(params: {
  hasContent?: boolean
  availableFrom?: string | null
  availableUntil?: string | null
  isPastByPosition?: boolean
}): DropState {
  const now = new Date()
  const availableFrom = toDate(params.availableFrom)
  const availableUntil = toDate(params.availableUntil)

  if (availableUntil && now > availableUntil) {
    return 'past'
  }

  if (availableFrom && now < availableFrom) {
    return 'pending'
  }

  if (params.isPastByPosition) {
    return 'past'
  }

  if (!availableFrom && !availableUntil && params.hasContent === false) {
    return 'pending'
  }

  return 'latest'
}

const CollectibleCard = ({
  title,
  quarter,
  image,
  href,
  hasContent,
  availableFrom,
  availableUntil,
  backgroundColour,
  isPastByPosition,
  ...props
}: CollectibleProps) => {
  const quarterCode = getQuarterCode(quarter)
  const quarterYear = getQuarterYear(quarter)
  const state = resolveDropState({
    hasContent,
    availableFrom,
    availableUntil,
    isPastByPosition,
  })

  const statusLabel =
    state === 'latest'
      ? 'Available Now'
      : state === 'past'
        ? 'Past Drop'
        : 'Coming Up'

  const cardBackground = backgroundColour
    ? mapColor(backgroundColour)
    : '$bgTertiary'

  return (
    <YStack gap="$2" {...props}>
      <YStack
        pos="relative"
        w="100%"
        bg={cardBackground}
        bw={0}
        br="$5"
        overflow="hidden"
        p="$4"
      >
        <SmartLink
          Component={YStack}
          pos="relative"
          animation="medium"
          w="100%"
          aspectRatio={1}
          tabIndex={-1}
          {...(href && hasContent && { href, ...interaction.thumb })}
        >
          <SolitoImage
            src={image}
            fill
            contentFit="cover"
            sizes="(min-width: 768px) 100vw, 350px"
            alt="Thumbnail"
          />
        </SmartLink>

        {!!quarterCode && (
          <YStack
            pos="absolute"
            bottom="$1.5"
            right="$2.5"
            zIndex="$1"
            ai="flex-end"
            gap="$0.25"
            pointerEvents="none"
          >
            <QuarterCodeText
              quarter={quarterCode}
              textColor={QUARTER_TEXT_COLOR}
              outlineColor="$fgPrimary"
              shadowOffset={{ width: 2, height: 1 }}
              textProps={{ size: '$8' }}
            />
            {!!quarterYear && (
              <SizableText size="$1" fow="700" color="$fgPrimary" opacity={0.8}>
                {quarterYear}
              </SizableText>
            )}
          </YStack>
        )}

        <Tag
          position="absolute"
          top="$2.5"
          left="$2.5"
          variant="neutral"
          displaySize="md"
          pointerEvents="none"
        >
          All Access
        </Tag>
      </YStack>

      <SmartLink
        Component={YStack}
        gap="$1"
        {...(href && hasContent && { href, ...interaction.opacity })}
      >
        <SizableText
          size="$m"
          fow="500"
          color="$fgPrimary"
          numberOfLines={2}
          $gtXs={{ size: '$l', fow: '500' }}
        >
          {title}
        </SizableText>
        <SizableText size="$s" color="$fgSecondary" opacity={0.6}>
          {statusLabel}
        </SizableText>
      </SmartLink>
    </YStack>
  )
}

export default CollectibleCard
